import { Module } from 'vuex'
import { chatDB, dbUtils, ChatHistory, ChatMessage } from '@/store/modules/chat/database'
import store from '@/store'
import { globalEmit, EVENT_TYPES } from '@/utils/eventBus'
import { $t } from '@/utils/index.ts'
import { CompressedFile } from '@/utils/imageCompress'
import { like } from '@/api/chat/chat'
import { parseMessageItem } from '@/utils/chat'
import { logEvent } from '@/utils/utils.ts'

// 聊天状态接口
export interface ChatState {
    isFirstLaunch: boolean
    answerOnTop: boolean
    chatWithText: string
    chatWithImage: CompressedFile[]
    // 当前活跃会话
    historyList: ChatHistory[]
    chatStatus: 'pending' | 'thinking' | 'receiving' | 'success' | 'error'
    // footer高度
    footerHeight: number
    // 当前请求的AbortController
    currentAbortController: AbortController | null
    // 欢迎消息是否准备好
    welcomeReady: boolean
    // 消息发送起始时间
    responseStartTime: number | null
}

const state: ChatState = {
    isFirstLaunch: true, //是否首次进入项目 暂时没用上
    answerOnTop: false, //Answer on top
    chatWithText: '',
    chatWithImage: [],
    historyList: [],
    chatStatus: 'success',
    footerHeight: 135,
    currentAbortController: null,
    welcomeReady: true,
    responseStartTime: null
}

// Getters
const getters = {
    isFirstLaunch: (state: ChatState) => state.isFirstLaunch,

    historyList: (state: ChatState) => state.historyList,

    displayHistoryList: (state: ChatState) => state.historyList,

    // 获取最后一条消息
    lastMessage: (state: ChatState) => {
        const history = state.historyList.find(h => h.id === getters.lastHistoryId(state))

        return history?.messageList ? history?.messageList[history?.messageList.length - 1] : null
    },

    sessionId: (state: ChatState) => {
        const history = state.historyList.find(h => h.id === getters.lastHistoryId(state))

        return history?.messageList.filter(item => !!item.sessionId)[0]?.sessionId
    },

    lastHistoryId: (state: ChatState) => {
        return state.historyList[state.historyList.length - 1]?.id
    },

    lastHistory: (state: ChatState) => {
        return state.historyList[state.historyList.length - 1]
    },

    // 检查是否可以发送消息
    canSendMessage: (state: ChatState) => {
        return state.chatStatus == 'success' || state.chatStatus == 'error'
    },

    // 是否显示stop
    showStop: (state: ChatState) => {
        return state.chatStatus == 'thinking' || state.chatStatus == 'receiving' || state.chatStatus == 'pending'
    },

    answerOnTop: (state: ChatState) => state.answerOnTop,

    // 获取footer高度
    footerHeight: (state: ChatState) => state.footerHeight,

    // 获取聊天内容
    chatWithText: (state: ChatState) => state.chatWithText,

    // 获取聊天图片
    chatWithImage: (state: ChatState) => state.chatWithImage,

    // 获取当前AbortController
    currentAbortController: (state: ChatState) => state.currentAbortController,

    showWelcome(_: ChatState, getters: any) {
        try {
            return getters.displayHistoryList?.length == 1 && getters.displayHistoryList[0]?.messageList?.length == 1 && getters.displayHistoryList[0].messageList[0].type == 'welcome'
        } catch (err) {
            return false
        }
    }
}

// Mutations
const mutations = {
    ADD_HISTORY(state: ChatState, history: ChatHistory) {
        state.historyList.push(history)
    },

    // 设置历史记录列表
    SET_HISTORY_LIST(state: ChatState, historyList: ChatHistory[]) {
        state.historyList = historyList.map(historyItem => {
            historyItem.messageList = historyItem.messageList.map(messageItem => {
                return parseMessageItem(messageItem)
            })

            return historyItem
        })
    },

    // 添加消息
    ADD_MESSAGE_TO_HISTORY(state: ChatState, message: ChatMessage) {
        const history = state.historyList.find(h => h.id === getters.lastHistoryId(state))

        if (history) {
            history.messageList.push(message)
        }
    },

    // 改变聊天状态
    CHANGE_CHAT_STATUS(state: ChatState, status: ChatState['chatStatus']) {
        state.chatStatus = status
    },

    // 更新消息
    UPDATE_MESSAGE(state: ChatState, { id, hId, message }: { id: number; hId: number; message: ChatMessage }) {
        const history = state.historyList.find(h => h.id === (hId || getters.lastHistoryId(state)))
        const messageIndex = history?.messageList.findIndex(msg => msg.id === id)

        if (history && messageIndex != null) {
            history.messageList[messageIndex] = { ...history.messageList[messageIndex], ...message }
        }
    },

    // 设置footer高度
    SET_FOOTER_HEIGHT(state: ChatState, height: number) {
        state.footerHeight = height
    },

    SET_CHAT_WITH_TEXT(state: ChatState, text: string) {
        state.chatWithText = text
    },

    SET_CHAT_WITH_IMAGE(state: ChatState, compressedFiles: CompressedFile[]) {
        state.chatWithImage = compressedFiles
    },

    SET_CURRENT_ABORT_CONTROLLER(state: ChatState, controller: AbortController | null) {
        state.currentAbortController = controller
    },

    SET_ANSWER_ON_TOP(state: ChatState, answerOnTop: boolean) {
        state.answerOnTop = answerOnTop
    },
    SET_WELCOME_READY(state: ChatState, welcomeReady: boolean) {
        state.welcomeReady = welcomeReady
    }
}

// Actions
const actions = {
    // 初始化数据库
    async initDatabase({ dispatch }: any) {
        try {
            const success = await chatDB.initDatabase()

            if (success) {
                await dispatch('loadHistoryList')

                globalEmit(EVENT_TYPES.CHAT_HISTORY_LOADED)
            } else {
                throw new Error('Init database failed')
            }
        } catch (error) {
            console.error('Init database failed:', error)
            throw new Error('Init database failed')
        }
    },

    // 加载会话列表
    async loadHistoryList({ commit }: any) {
        try {
            const historyList = await dbUtils.getUserHistories({ type: 'init' })

            commit('SET_HISTORY_LIST', historyList)
        } catch (error) {
            console.error('Init history list failed:', error)
        }
    },

    /**
     * @description 创建新会话 [拍题(上传),推荐问题,上一个历史记录回复超过30条并且有sessionId(这个暂时没做,需要确认需求TODO),手动增加,]
     * @param title 新历史记录的标题
     * @returns
     */
    async createNewHistory({ commit }: any, { title, type }: { title: string; type: string }) {
        try {
            const { hId, history } = await dbUtils.createHistory(title, type)

            commit('ADD_HISTORY', history)

            return hId
        } catch (error) {
            console.error('创建新会话失败:', error)
        }
    },

    // 添加消息
    async addMessageToHistory({ commit }: any, message: ChatMessage) {
        try {
            const historyId = store.getters['chat/lastHistoryId']

            message.like = message.like == undefined ? 0 : message.like
            message.hId = historyId

            const id = await dbUtils.addMessageToHistory(historyId, message)

            commit('ADD_MESSAGE_TO_HISTORY', message)

            return { messageId: id, historyId }
        } catch (error) {
            console.error('添加消息到会话失败:', error)
        }
    },

    // 改变聊天状态
    changeChatStatus({ commit }: any, status: ChatState['chatStatus']) {
        if (status === 'thinking') {
            state.responseStartTime = Date.now()
        }
        commit('CHANGE_CHAT_STATUS', status)
    },

    // 追加消息内容
    async appendMessageContent({ commit }: any, message: ChatMessage) {
        const history = state.historyList.find(h => h.id === getters.lastHistoryId(state))
        const lastMessage = history?.messageList ? history?.messageList[history?.messageList.length - 1] : null

        if (history?.id && lastMessage) {
            const currentContent = lastMessage.content || ''
            const deltaContent = message.content || ''
            const newContent = currentContent + deltaContent

            await commit('UPDATE_MESSAGE', { id: lastMessage.id, message: { content: newContent, status: 'receiving' } })
        }
    },

    async completeMessage({ dispatch }: any, message: any = { status: 'success' }) {
        const history = state.historyList.find(h => h.id === getters.lastHistoryId(state))
        const lastMessage = history?.messageList ? history?.messageList[history?.messageList.length - 1] : null

        try {
            const sendMessage = history?.messageList.find(item => item.id == lastMessage?.relativeId)

            logEvent('web_chat_message_reply_complete', {
                ev: 'output finished',
                type: sendMessage?.type,
                logTimerKey: 'pickAndChat',
                sessionId: lastMessage?.sessionId,
                sessionMessageId: lastMessage?.sessionMessageId,
                clipId: message.image?.clipId,
                response_duration_ms: state.responseStartTime ? Date.now() - state.responseStartTime : undefined
            })
        } catch (error) {
            console.error('Complete message log event failed:', error)
        }

        if (history?.id && lastMessage) {
            await dbUtils.updateMessageInHistory(history.id, lastMessage.id, { content: lastMessage.content, status: message.status })

            dispatch('updateLastMessage', { status: message.status, errorData: message.errorData })
            dispatch('changeChatStatus', 'success')

            // Firestore 实时推送的兜底：若监听失败/延迟，仍通过 API 确保 sparks 显示正确
            if (store.getters['user/isLoggedIn']) {
                store.dispatch('user/refreshUserInfo')
            }
        }
    },

    // 改变聊天状态
    async updateLastMessage({ commit }: any, message: ChatMessage) {
        const lastHistoryId = getters.lastHistoryId(state)
        const history = state.historyList.find(h => h.id === lastHistoryId)
        const lastMessage = history?.messageList ? history?.messageList[history?.messageList.length - 1] : null

        if (history?.id && lastMessage) {
            await commit('UPDATE_MESSAGE', { id: lastMessage.id, message })
            await dbUtils.updateMessageInHistory(history.id, lastMessage.id, message)
        }
    },

    // 跟新聊天图片
    async updateLastSendImageMessage({ commit }: any, message: ChatMessage) {
        const lastHistoryId = getters.lastHistoryId(state)
        const history = state.historyList.find(h => h.id === lastHistoryId)
        const lastMessage = history?.messageList ? history?.messageList[history?.messageList.length - 2] : null

        if (history?.id && lastMessage) {
            await commit('UPDATE_MESSAGE', { id: lastMessage.id, message })
            await dbUtils.updateMessageInHistory(history.id, lastMessage.id, message)
        }
    },

    // 修改点赞状态
    async updateMessageLike({ commit }: any, { messageItem, type }: { messageItem: ChatMessage; type: number }) {
        await commit('UPDATE_MESSAGE', { id: messageItem.id, hId: messageItem.hId, message: { like: type } })
        await dbUtils.updateMessageInHistory(messageItem.hId, messageItem.id, { like: type })
        like(messageItem.sessionMessageId || 0, type)
    },

    getMessageById({ commit }: any, message: ChatMessage) {
        const historyItem = state.historyList.find(h => h.id === message.relativeHId)
        const messageItem = historyItem?.messageList.find(m => m.id === message.relativeId)

        return messageItem
    },

    // 设置footer高度
    setFooterHeight({ commit }: any, height: number) {
        commit('SET_FOOTER_HEIGHT', height)
    },

    setChatWithText({ commit }: any, text: string) {
        commit('SET_CHAT_WITH_TEXT', text)
    },

    setChatWithImage({ commit }: any, compressedFiles: CompressedFile[]) {
        commit('SET_CHAT_WITH_IMAGE', compressedFiles)
    },

    setCurrentAbortController({ commit }: any, controller: AbortController | null) {
        commit('SET_CURRENT_ABORT_CONTROLLER', controller)
    },

    // 创建新的AbortController
    createAbortController({ commit }: any) {
        const controller = new AbortController()
        commit('SET_CURRENT_ABORT_CONTROLLER', controller)
        return controller
    },

    // 中止当前请求
    abortCurrentRequest({ commit, state }: any) {
        try {
            console.warn('Start cancel receive message', state.currentAbortController)

            if (state.currentAbortController) {
                state.currentAbortController.abort()
                commit('SET_CURRENT_ABORT_CONTROLLER', null)

                globalEmit(EVENT_TYPES.CHAT_ABORT_REQUEST)
            }
        } catch (error) {
            console.error('Abort current request failed:', error)
        }

        return true
    },

    // 清理AbortController
    clearAbortController({ commit }: any) {
        commit('SET_CURRENT_ABORT_CONTROLLER', null)
    },

    setAnswerOnTop({ commit }: any, answerOnTop: boolean) {
        commit('SET_ANSWER_ON_TOP', answerOnTop)
    },

    getSnapHistoryList({ commit }: any, pageInfo: any) {
        return dbUtils.getSnapHistoryList(pageInfo)
    },
    async updateWelcomeMessage({ commit }: any, welcomeStream: string) {
        const lastHistoryId = getters.lastHistoryId(state)
        const history = state.historyList.find(h => h.id === lastHistoryId)
        const lastMessage = history?.messageList ? history?.messageList[history?.messageList.length - 1] : null

        if (history?.id && lastMessage) {
            await commit('UPDATE_MESSAGE', { id: lastMessage.id, message: { content: welcomeStream } })
            await dbUtils.updateMessageInHistory(history.id, lastMessage.id, { content: welcomeStream })
        }
    }
}

const chatModule: Module<ChatState, any> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}

export default chatModule
